/* Core & libraries */
import React, { useState, useRef,Suspense, lazy } from "react";
import { useTheme } from "react-jss";
import { Link } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { storageFactory } from "factory/storageFactory";

/* Helpers, constants, custom methods, internal libraries */
import { useLocaleContext } from "../../contexts/localeContext";
import { useUserContext } from "../../contexts/userContext";
import {
  getHelpUrl,
  bookTravelUrl,
  nameInitials,
  getTermsLink,
  getPolicyLink,
  getFullLanguageText,
} from '../../helpers/general';
import { useGeneralService } from "../../services/generalService";
import {
  AIRLINES,
  API_HOST,
  API_PROTOCOL,
  ARC_POLICY_LINK,
  CHOICE_TERMS_OF_USE_LINK,
} from '../../constants/appConstants';
import { useHasPermission } from "../../hooks/useHasPermission";
import { getDismissableBannerKey } from "../../helpers/general";
import { bannerPresent } from "../../helpers/general";
import { lazyWithPreload } from '../../helpers/lazyLoadingHelpers';
import { useFormatLocale } from '../../hooks/useFormatLocale';

/* Components */
import { ReactComponent as HelpSvg } from '../../resources/images/help.svg';
import { ReactComponent as ActiveBookTravelSvg } from '../../resources/images/as-book-travel-active.svg';
import { ReactComponent as ChoiceBookTravelSvg } from '../../resources/images/book-travel-choice.svg';
import { ReactComponent as ActiveHelpSvg } from '../../resources/images/aa-help-active.svg';
import { ReactComponent as ActiveHelpSvgUC } from '../../resources/images/uc-help-active.svg';
import { ReactComponent as ActiveHelpSvgAS } from '../../resources/images/as-help-active.svg';
import { ReactComponent as ActiveHelpSvgAC } from '../../resources/images/as-help-active.svg';
import { ReactComponent as ActiveHelpSvgCHOICE } from '../../resources/images/aa-help-active.svg';
import { ReactComponent as DotsSvg } from '../../resources/images/dots-horizontal.svg';
import { ReactComponent as SignOutIcon } from '../../resources/images/logout.svg';
import { ReactComponent as SignOutAdminIcon } from '../../resources/images/logout-admin.svg';
import { ReactComponent as ActiveLangSvg } from '../../resources/images/aa-language-active.svg';
import { ReactComponent as ActiveLangSvgUC } from '../../resources/images/uc-language-active.svg';
import { ReactComponent as ActiveLangSvgAS } from '../../resources/images/as-language-active.svg';
import { ReactComponent as LangSvgCHOICE } from '../../resources/images/choice-language.svg';
import { ReactComponent as ActiveLangSvgCHOICE } from '../../resources/images/choice-language-active.svg';
import { ReactComponent as SwitchSvg } from '../../resources/images/switch-horizontal.svg';
import CompaniesSwitchModal from "../modals/companiesSwitchModal";
import NavigationHelpACModal from "../modals/navigationHelpACModal";
const CountryLanguageModalLazy = lazyWithPreload(() => import('../modals/countryLanguageModal'));
import FlagIconComponent from '../flagIconComponent';
import UCSelect from "../ucselect";
import AcTermsModal from "../modals/acTermsAndConditions";

/* Styles */
import styles, { selectPrivacyStyle } from './styles.js';


const localStore = storageFactory(() => localStorage);
const sessionStore = storageFactory(() => sessionStorage);

const NavigationFooter = (props) => {
  const { clickOnLink, isAdmin, bannersHeight } = props;

  const theme = useTheme();
  const classes = styles({ theme });

  const [isHover, setIsHover] = useState(null);
  const locale = useLocaleContext();
  const userDetails = useUserContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;
  const { changeCompany } = useGeneralService();
  const { checkPermission } = useHasPermission();

  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showCompaniesSwitchModal, setShowCompaniesSwitchModal] = useState(false);
  const [showHelpACModal, setShowHelpACModal] = useState(false);
  const [showAcTermsModal, setShowAcTermsModal] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false)
  const { locales } = useFormatLocale();

  const activeHelpIcon = {
    AA: <ActiveHelpSvg />,
    UC: <ActiveHelpSvgUC />,
    AS: <ActiveHelpSvgAS />,
    AM: <ActiveHelpSvgAS/>,//todo: update this
    CHOICE: <ActiveHelpSvgCHOICE/>,
    AC: <ActiveHelpSvgAC/>,//todo: update this
  };

  const handleMouseEnter = (key) => {
    setIsHover(key);
    CountryLanguageModalLazy.preloadFn();
  };

  const handleMouseLeave = () => {
    setIsHover(null);
  };
  const logout = () => {
    const LOGOUT_URL = "oidc/logout.html"
    window.location.href = `${API_PROTOCOL}://${API_HOST}/${LOGOUT_URL}`;
    localStore.removeItem("showAuthorisedCityUsersModal")
    sessionStore.removeItem(getDismissableBannerKey(providerCode))
  };

  const switchCompanies = () => {
    document.getElementById("root").click();
    setShowCompaniesSwitchModal(true)
  };

  let key;

  const renderPopover = () => {
    return (
      <Popover className={classes.popoverStyle} tabIndex={0}>
        <div className={classes.popoverDiv}>
          {userDetails.profileSelector &&
            userDetails.profileSelector.length > 1 && (
              <Link onClick={switchCompanies} className={classes.overlayLink} tabIndex={0}>
                <SwitchSvg className={classes.popoverImage} />
                <FormattedMessage id='navigation.switchCompany' />
              </Link>
            )}
          <Link onClick={logout} className={classes.overlayLink} tabIndex={0}>
            <SignOutIcon className={classes.popoverImage} />
            <FormattedMessage id='navigation.signOut' />
          </Link>
        </div>
      </Popover>
    );
  };

  const applyBannerStyling = bannerPresent(providerCode) && sessionStore.getItem(getDismissableBannerKey(providerCode)) !== 'dismissed'

  const privacyOption = [
    ...(providerCode === 'CHOICE' ? [{
      label: <FormattedMessage id="privacyOption.websiteTerms" />,
      value: "1",
      url: CHOICE_TERMS_OF_USE_LINK
    }] : []),
    {
      label: <FormattedMessage id="privacyOption.programTerms" />,
      value: "2",
      ...(providerCode === 'AC' ? { onSelect: () => setShowAcTermsModal(true) } : { url: getTermsLink(providerCode) })
    },
    {
      label: <FormattedMessage id="navigation.CHOICE.privacyPolicy" />,
      value: "3",
      url: getPolicyLink(providerCode)
    },
  ]

  const handlePrivacyChange = (selected) => {
    if (selected?.onSelect) {
      selected.onSelect();
    } else if (selected?.url) {
      const url = selected.url;
      setTimeout(() => window.open(url, "_blank", "noopener,noreferrer"), 0);
    }
  }
  return (
    <div
      className={`${isAdmin ? classes.adminfooter : ''} ${userDetails?.manageCompany ? classes.manageCompanyfooter : ''} ${classes.footer}`}
      data-testid={'navigationfooter'}
      style={bannersHeight > 0 ? { marginBottom: `${bannersHeight}px` } : undefined}
    >
      {bookTravelUrl(providerCode) && (
        <div className={classes.bookTravel}>
          <Link
            to={bookTravelUrl(providerCode)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={clickOnLink}
            data-testid="booktravellink"
          >
            {providerCode === 'CHOICE' ? <ChoiceBookTravelSvg /> : <ActiveBookTravelSvg />}
            <div>
              <FormattedMessage id="navigation.bookTravel" />
            </div>
          </Link>
        </div>
      )}
      {providerCode !== 'AS' && (
        <div
          className={`${classes.lang} ${classes.companyLang}`}
          onMouseEnter={() => handleMouseEnter(1)}
          onMouseLeave={handleMouseLeave}
        >
          <div
            tabIndex="0"
            onClick={() => setShowLanguageModal(true)}
            className="langSelector"
            data-testid={'langselector'}
          >
            <FlagIconComponent locale={locale} />
            <div className={classes.locale}>{getFullLanguageText(locale)}</div>
          </div>
        </div>
      )}

      {checkPermission('F_VIEW_HELPLINKS') && !isAdmin && (
        <div className={classes.help} onMouseEnter={() => handleMouseEnter(2)} onMouseLeave={handleMouseLeave}>
          <Link
            {...(providerCode === 'AC'
              ? { onClick: () => { clickOnLink?.(); setShowHelpACModal(true); } }
              : { to: getHelpUrl(providerCode), target: '_blank', rel: 'noopener noreferrer', onClick: clickOnLink }
            )}
            data-testid="helplink"
          >
            {isHover === 2 ? activeHelpIcon[providerCode] : <HelpSvg />}
            <div>
              {providerCode === 'CHOICE' ? (
                <FormattedMessage id="navigation.CHOICE.help" />
              ) : (
                <FormattedMessage id="navigation.help" />
              )}
            </div>
          </Link>
        </div>
      )}
      {
        (providerCode === 'CHOICE' || providerCode === 'UC' || providerCode === 'AC') && (

          <div className={classes.privacyDiv}>
            <UCSelect
              className={classes.privacyStyle}
              styles={selectPrivacyStyle(theme)}
              displayName={<FormattedMessage id="privacyOption.legalPrivacy" />}
              options={privacyOption}
              value={null}
              onChange={handlePrivacyChange}
              shouldOpenUp={isAdmin || userDetails?.manageCompany}
            />
            {
              providerCode === 'CHOICE' &&
                <div className={classes.poweredBy}>
                  <FormattedMessage id="privacyOption.poweredBy" />
                  <Link
                    to={ARC_POLICY_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={clickOnLink}
                    className={classes.arcLink}
                  >
                    <FormattedMessage id="privacyOption.link" />
                  </Link>
                </div>
            }
          </div>
        )
      }

      {!isAdmin && !userDetails?.manageCompany && providerCode !== 'CHOICE' && (
        <div className={classes.links}>
          {checkPermission('F_VIEW_PRIVACYPOLICY') && ( //todo: replace with the new permission
            <div className={classes.terms} onMouseEnter={() => handleMouseEnter(3)} onMouseLeave={handleMouseLeave}>
              <Link
                to={getTermsLink(providerCode)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={clickOnLink}
                data-testid={'termslink'}
              >
                <div>
                  <FormattedMessage id={`navigation.${providerCode}.terms`} />
                </div>
              </Link>
            </div>
          )}
          {checkPermission('F_VIEW_PRIVACYPOLICY') && (providerCode !== AIRLINES.AM) && (
            <div className={classes.terms} onMouseEnter={() => handleMouseEnter(4)} onMouseLeave={handleMouseLeave}>
              <Link
                to={getPolicyLink(providerCode)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={clickOnLink}
                data-testid={'privacypolicylink'}
              >
                <div>
                  <FormattedMessage id={`navigation.${providerCode}.privacyPolicy`} />
                </div>
              </Link>
            </div>
          )}
        </div>
      )}
      {isAdmin ? (
        <div
          className={`${classes.profileAdmin} ${applyBannerStyling ? classes.footerBannerProfileStyle : ''}`}
          tabIndex={0}
        >
          <Link onClick={logout} className={classes.adminLogoutLink}>
            <SignOutAdminIcon className={classes.popoverImage} />
            <FormattedMessage id="navigation.signOut" />
          </Link>
        </div>
      ) : (
        !userDetails?.manageCompany && (
          <div
            className={`${classes.profile} ${applyBannerStyling ? classes.footerBannerProfileStyle : ''} ${bannersHeight > 0 ? classes.profileBannerPadding : ''}`}
            tabIndex={0}
          >
            <div className={classes.profileAvatar}>
              <span data-testid="nameinitials">
                {nameInitials(`${userDetails?.userInfo.firstName} ${userDetails?.userInfo.lastName}`)}
              </span>
            </div>
            <div className={classes.profileDetails}>
              <p>{`${userDetails?.userInfo.firstName} ${userDetails?.userInfo.lastName}`}</p>
              <p>
                {userDetails?.userInfo?.companyInfo?.companyName || userDetails?.userInfo.providerInfo.providerName}
              </p>
            </div>
            <div className={classes.profileAction}>
              <OverlayTrigger
                trigger="click"
                rootClose
                placement="top"
                overlay={renderPopover()}
                onEnter={() => setOverlayOpen(true)}
                onExit={() => setOverlayOpen(false)}
                onEntered={(node) => node.focus()}
              >
                <div
                  className={`${classes.dotsStyle} ${overlayOpen ? classes.dotsOpenStyle : ''}`}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.target.click();
                    }
                  }}
                >
                  <DotsSvg />
                </div>
              </OverlayTrigger>
            </div>
          </div>
        )
      )}

      <NavigationHelpACModal showModal={showHelpACModal} setShowModal={setShowHelpACModal} />
      <AcTermsModal showTermsModal={showAcTermsModal} setShowTermsModal={setShowAcTermsModal} />
      <CompaniesSwitchModal
        showModal={showCompaniesSwitchModal}
        setShowModal={setShowCompaniesSwitchModal}
        changeCompany={changeCompany}
        className={classes.popoverStyle}
      />
      <Suspense fallback={null}>
        {showLanguageModal && (
          <CountryLanguageModalLazy showModal={showLanguageModal} setShowModal={setShowLanguageModal} locales={locales}/>
        )}
      </Suspense>
    </div>
  );
}

export default NavigationFooter;
